<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ReportHeader from './components/ReportHeader.vue';
import SearchInput from 'dashboard/modules/search/components/SearchInput.vue';
import SearchDateRangeSelector from 'dashboard/modules/search/components/SearchDateRangeSelector.vue';
import SearchContactAgentSelector from 'dashboard/modules/search/components/SearchContactAgentSelector.vue';
import SearchInboxSelector from 'dashboard/modules/search/components/SearchInboxSelector.vue';
import SearchResultMessagesList from 'dashboard/modules/search/components/SearchResultMessagesList.vue';
import SearchAPI from 'dashboard/api/search';
import {
  generateURLParams,
  parseURLParams,
} from 'dashboard/modules/search/helpers/searchHelper';

const PER_PAGE = 15;

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const query = ref(route.query.q || '');
const currentPage = ref(1);
const hasCompletedSearch = ref(false);
const direction = ref(route.query.direction || 'all');
const viewMode = ref(route.query.view || 'all');
const qualitySummary = ref([]);
const filters = ref(parseURLParams(route.query, true));

const messageRecords = useMapGetter('conversationSearch/getMessageRecords');
const uiFlags = useMapGetter('conversationSearch/getUIFlags');

const messages = computed(() =>
  messageRecords.value.map(item => useCamelCase(item, { deep: true }))
);

const fallbackQualitySummary = computed(() => {
  const groupedRows = messages.value.reduce((acc, message) => {
    const assigneeId = message.assigneeId || 'unassigned';
    const agentName =
      message.assigneeName || t('SEARCH.QUALITY.UNASSIGNED_AGENT');

    if (!acc[assigneeId]) {
      acc[assigneeId] = {
        assigneeId: assigneeId === 'unassigned' ? null : assigneeId,
        agentName,
        totalCount: 0,
        incomingCount: 0,
        outgoingCount: 0,
      };
    }

    acc[assigneeId].totalCount += 1;

    if (message.messageType === 0) {
      acc[assigneeId].incomingCount += 1;
    } else if ([1, 3].includes(message.messageType)) {
      acc[assigneeId].outgoingCount += 1;
    }

    return acc;
  }, {});

  return Object.values(groupedRows).sort(
    (left, right) =>
      right.totalCount - left.totalCount ||
      left.agentName.localeCompare(right.agentName)
  );
});

const displayedQualitySummary = computed(() =>
  qualitySummary.value.length ? qualitySummary.value : fallbackQualitySummary.value
);

const isFetching = computed(() => uiFlags.value.message.isFetching);

const directionOptions = computed(() => [
  { value: 'all', label: t('SEARCH.QUALITY.DIRECTION_OPTIONS.ALL') },
  { value: 'incoming', label: t('SEARCH.QUALITY.DIRECTION_OPTIONS.INCOMING') },
  { value: 'outgoing', label: t('SEARCH.QUALITY.DIRECTION_OPTIONS.OUTGOING') },
]);

const viewOptions = computed(() => [
  { value: 'all', label: t('SEARCH.QUALITY.VIEW_OPTIONS.ALL') },
  { value: 'agent', label: t('SEARCH.QUALITY.VIEW_OPTIONS.AGENT') },
]);

const hasQuery = computed(() => Boolean(query.value?.trim()));
const selectedAgentId = computed(() => {
  const value = filters.value.from;
  if (!value?.startsWith('agent:')) return undefined;

  const [, id] = value.split(':');
  const numericId = Number(id);
  return Number.isNaN(numericId) || numericId <= 0 ? undefined : numericId;
});

const hasActiveFilters = computed(
  () =>
    filters.value.from ||
    filters.value.in ||
    filters.value.dateRange?.type ||
    filters.value.dateRange?.from ||
    filters.value.dateRange?.to
);

const showLoadMore = computed(
  () =>
    hasQuery.value &&
    !isFetching.value &&
    messages.value.length > 0 &&
    messages.value.length === currentPage.value * PER_PAGE
);

const showEmptyState = computed(
  () =>
    hasQuery.value &&
    hasCompletedSearch.value &&
    !isFetching.value &&
    (viewMode.value === 'agent'
      ? displayedQualitySummary.value.length === 0
      : messages.value.length === 0)
);

const updateURL = () => {
  const queryParams = {
    ...(query.value?.trim() && { q: query.value.trim() }),
    direction: direction.value,
    view: viewMode.value,
    ...generateURLParams(filters.value, true),
  };

  router.replace({
    name: 'quality_reports',
    params: { accountId: route.params.accountId },
    query: queryParams,
  });
};

const clearResults = () => {
  currentPage.value = 1;
  hasCompletedSearch.value = false;
  qualitySummary.value = [];
  store.dispatch('conversationSearch/clearSearchResults');
};

const buildSearchPayload = page => ({
  q: query.value.trim(),
  page,
  since: filters.value.dateRange?.from || undefined,
  until: filters.value.dateRange?.to || undefined,
  assigneeId: selectedAgentId.value,
  inboxId: filters.value.in || undefined,
  qualityMode: true,
  messageDirection: direction.value,
});

const buildSummaryPayload = () => ({
  q: query.value.trim(),
  since: filters.value.dateRange?.from || undefined,
  until: filters.value.dateRange?.to || undefined,
  assigneeId: selectedAgentId.value,
  inboxId: filters.value.in || undefined,
  messageDirection: direction.value,
});

const executeSearch = async ({ reset = true } = {}) => {
  if (!hasQuery.value) {
    clearResults();
    updateURL();
    return;
  }

  if (reset) {
    clearResults();
  }

  updateURL();
  await store.dispatch(
    'conversationSearch/messageSearch',
    buildSearchPayload(currentPage.value)
  );

  if (viewMode.value === 'agent') {
    const { data } = await SearchAPI.qualitySummary(buildSummaryPayload());
    const rows = data.payload?.agents || data.agents || [];
    qualitySummary.value = rows.map(item =>
      useCamelCase(item, { deep: true })
    );
  }
  hasCompletedSearch.value = true;
};

const onSearch = async value => {
  query.value = value || '';
  await executeSearch({ reset: true });
};

const onFilterChange = async () => {
  if (!hasQuery.value) {
    updateURL();
    return;
  }

  await executeSearch({ reset: true });
};

const onDirectionChange = async value => {
  direction.value = value || 'all';
  await executeSearch({ reset: true });
};

const onViewChange = async value => {
  viewMode.value = value || 'all';
  await executeSearch({ reset: true });
};

const loadMore = async () => {
  if (viewMode.value === 'agent') return;
  if (!showLoadMore.value) return;

  currentPage.value += 1;
  await executeSearch({ reset: false });
};

onMounted(async () => {
  store.dispatch('conversationSearch/clearSearchResults');
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');

  if (hasQuery.value) {
    await executeSearch({ reset: true });
  }
});

onUnmounted(() => {
  store.dispatch('conversationSearch/clearSearchResults');
});
</script>

<template>
  <div class="flex flex-col gap-5 pb-8">
    <ReportHeader
      :header-title="$t('SEARCH.QUALITY.HEADER')"
      :header-description="$t('SEARCH.QUALITY.DESCRIPTION')"
    />

    <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
      <SearchInput v-model="query" @search="onSearch">
        <div
          class="flex flex-col lg:flex-row items-start lg:items-center gap-3 p-4 w-full min-w-0"
        >
          <SearchDateRangeSelector
            v-model="filters.dateRange"
            class="min-w-0 max-w-full"
            @change="onFilterChange"
          />

          <div class="w-px h-4 bg-n-weak flex-shrink-0 hidden lg:block" />

          <div class="flex items-center gap-2 min-w-0 max-w-full flex-wrap">
            <span class="text-sm text-n-slate-10 whitespace-nowrap">
              {{ t('SEARCH.QUALITY.AGENT_LABEL') }}
            </span>
            <SearchContactAgentSelector
              v-model="filters.from"
              mode="agent"
              :label="$t('SEARCH.QUALITY.AGENT_PLACEHOLDER')"
              @change="onFilterChange"
            />

            <div class="w-px h-3 bg-n-weak rounded-lg flex-shrink-0" />

            <span class="text-sm text-n-slate-10 whitespace-nowrap">
              {{ t('SEARCH.QUALITY.FILTER_LABEL') }}
            </span>
            <SearchInboxSelector
              v-model="filters.in"
              :label="$t('SEARCH.FILTERS.IN')"
              @change="onFilterChange"
            />

            <div class="w-px h-3 bg-n-weak rounded-lg flex-shrink-0" />

            <span class="text-sm text-n-slate-10 whitespace-nowrap">
              {{ t('SEARCH.QUALITY.DIRECTION_LABEL') }}
            </span>
            <ComboBox
              v-model="direction"
              :options="directionOptions"
              :placeholder="$t('SEARCH.QUALITY.DIRECTION_PLACEHOLDER')"
              class="min-w-[12rem] [&>div>button]:bg-n-alpha-black2"
              @update:model-value="onDirectionChange"
            />

            <div class="w-px h-3 bg-n-weak rounded-lg flex-shrink-0" />

            <span class="text-sm text-n-slate-10 whitespace-nowrap">
              {{ t('SEARCH.QUALITY.VIEW_LABEL') }}
            </span>
            <ComboBox
              v-model="viewMode"
              :options="viewOptions"
              :placeholder="$t('SEARCH.QUALITY.VIEW_PLACEHOLDER')"
              class="min-w-[13rem] [&>div>button]:bg-n-alpha-black2"
              @update:model-value="onViewChange"
            />
          </div>
        </div>
      </SearchInput>
    </div>

    <div v-if="hasQuery" class="text-sm text-n-slate-11">
      <span v-if="hasActiveFilters">{{ $t('SEARCH.QUALITY.ACTIVE_FILTERS') }}</span>
      <span v-else>{{ $t('SEARCH.QUALITY.NO_FILTERS') }}</span>
    </div>

    <SearchResultMessagesList
      v-if="hasQuery && viewMode === 'all'"
      :messages="messages"
      :query="query"
      :is-fetching="isFetching"
      :show-title="false"
      :show-empty-state="false"
    />

    <section
      v-if="hasQuery && viewMode === 'agent' && displayedQualitySummary.length"
      class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
    >
      <h3 class="mb-4 text-base font-semibold text-n-slate-12">
        {{ $t('SEARCH.QUALITY.AGENT_MATCHES_TITLE') }}
      </h3>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11 border-b border-n-weak">
              <th class="py-2 pr-3">{{ $t('SEARCH.QUALITY.TABLE.AGENT') }}</th>
              <th class="py-2 pr-3">{{ $t('SEARCH.QUALITY.TABLE.MATCHES') }}</th>
              <th class="py-2 pr-3">{{ $t('SEARCH.QUALITY.TABLE.INCOMING') }}</th>
              <th class="py-2">{{ $t('SEARCH.QUALITY.TABLE.OUTGOING') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in displayedQualitySummary"
              :key="`quality-agent-${row.assigneeId || 'unassigned'}-${row.agentName}`"
              class="border-b border-n-weak last:border-b-0"
            >
              <td class="py-2 pr-3 text-n-slate-12">{{ row.agentName }}</td>
              <td class="py-2 pr-3 text-n-slate-12">{{ row.totalCount }}</td>
              <td class="py-2 pr-3 text-n-slate-12">{{ row.incomingCount }}</td>
              <td class="py-2 text-n-slate-12">{{ row.outgoingCount }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="showLoadMore" class="flex justify-center">
      <NextButton
        :label="$t('SEARCH.LOAD_MORE')"
        icon="i-lucide-cloud-download"
        slate
        sm
        faded
        @click="loadMore"
      />
    </div>

    <div
      v-if="showEmptyState"
      class="flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <fluent-icon icon="search" size="24px" class="text-n-slate-11" />
      <p class="m-2 text-n-slate-11">
        {{ t('SEARCH.QUALITY.EMPTY_STATE_FULL', { query }) }}
      </p>
    </div>

    <div
      v-else-if="!hasQuery"
      class="flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <fluent-icon icon="search" size="24px" class="text-n-slate-11" />
      <p class="m-2 text-n-slate-11">
        {{ t('SEARCH.QUALITY.EMPTY_STATE_DEFAULT') }}
      </p>
    </div>
  </div>
</template>
